import PublicationTypes from "constants/publication-types";
import { DataTestAttributes } from "interfaces/data-test-attributes";
import PublicationRecord from "models/view-models/publication-record";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import React, { Fragment, useCallback } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import StringUtils from "utilities/string-utils";
import AnnexTableOfContentsRecord from "models/view-models/table-of-contents/annex-table-of-contents-record";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import NotebookTableOfContentsHeaderButton from "pages/notebooks/notebook-reference-panel/bookview/table-of-contents/notebook-table-of-contents-header-button";
import TableOfContentsCollapsePanelLinkWithinPanel from "pages/notebooks/notebook-reference-panel/bookview/table-of-contents/table-of-contents-collapse-panel-link-within-panel";
import VirtualTableOfContentsAnnexGroups from "molecules/virtual-table-of-contents-items/virtual-table-of-contents-annex-groups";
import { IconSizes } from "atoms/constants/icon-sizes";
import { useOpenTocPanels } from "pages/notebooks/notebook-reference-panel/use-open-toc-panels";

interface VirtualTableOfContentsAnnexProps
    extends Pick<DataTestAttributes, "dataTestId"> {
    allCollapsed: boolean;
    annexes: AnnexTableOfContentsRecord[];
    isPublic?: boolean;
    publication: PublicationRecord;
}

const CSS_CLASS_NAME = "c-virtual-table-of-contents";

const VirtualTableOfContentsAnnex: React.FunctionComponent<
    VirtualTableOfContentsAnnexProps
> = (props: VirtualTableOfContentsAnnexProps) => {
    const { annexes, dataTestId, isPublic = false, publication } = props;

    const { isPanelCollapsed } = useOpenTocPanels();

    const sortedAnnexes = annexes.sort(
        (a, b) => (a.displaySequence ?? 0) - (b.displaySequence ?? 0)
    );
    const panelTop = useCallback(
        (annex: AnnexTableOfContentsRecord) => {
            const annexTitle =
                publication?.type === PublicationTypes.NFC
                    ? annex.getDisplayTitle(publication.type)
                    : publication?.getAnnexesDisplayLabel();

            return (
                <NotebookTableOfContentsHeaderButton
                    dataTestId={dataTestId}
                    displayTitle={annexTitle}
                    isPublic={isPublic}
                    caapsId={annex.id}
                    publicationComponentType={PublicationComponentType.Annex}
                />
            );
        },
        [dataTestId, isPublic, publication]
    );

    if (publication.type !== PublicationTypes.NFC) {
        const firstAnnex = CollectionUtils.hasValues(sortedAnnexes)
            ? sortedAnnexes![0]
            : null;

        if (firstAnnex == null) {
            return null;
        }

        return (
            <div
                className={`${CSS_CLASS_NAME} -annex`}
                id={`${firstAnnex.id}_${PublicationComponentType.Annex}`}>
                <CollapsePanel
                    collapse={isPanelCollapsed(
                        firstAnnex.id!,
                        PublicationComponentType.Annex
                    )}
                    iconSize={IconSizes.Base}
                    buttonAriaText={publication.getAnnexesDisplayLabel()}
                    panelTop={panelTop(firstAnnex)}>
                    <nav className={`${CSS_CLASS_NAME}__annexNav`}>
                        <ul className={`${CSS_CLASS_NAME}__list`}>
                            {sortedAnnexes.map((annex) => (
                                <TableOfContentsCollapsePanelLinkWithinPanel
                                    key={annex.id}
                                    index={annex.id}
                                    isPublic={isPublic}
                                    publicationComponentType={
                                        PublicationComponentType.Annex
                                    }
                                    caapsId={annex.id}>
                                    {annex.getDisplayTitle()}
                                </TableOfContentsCollapsePanelLinkWithinPanel>
                            ))}
                        </ul>
                    </nav>
                </CollapsePanel>
            </div>
        );
    }

    return (
        <Fragment>
            {sortedAnnexes.map((annex) => {
                const hasGroup = CollectionUtils.hasValues(annex.annexGroups);

                const isEmptyAndPublicModifier =
                    !hasGroup && isPublic ? "-empty" : "";
                const className = StringUtils.joinClassNames([
                    CSS_CLASS_NAME,
                    isEmptyAndPublicModifier,
                ]);

                return (
                    <div
                        className={className}
                        key={annex.id}
                        id={`${annex.id}_${PublicationComponentType.Annex}`}>
                        <CollapsePanel
                            iconSize={IconSizes.Base}
                            buttonAriaText={annex.getDisplayTitle(
                                publication.type
                            )}
                            collapse={isPanelCollapsed(
                                annex.id!,
                                PublicationComponentType.Annex
                            )}
                            data-test-id="chapter-collapse-panel"
                            panelTop={panelTop(annex)}>
                            {hasGroup && (
                                <VirtualTableOfContentsAnnexGroups
                                    annexGroups={annex.annexGroups}
                                    annexNfpaLabel={annex.nfpaLabel ?? ""}
                                    isPublic={isPublic}
                                />
                            )}
                        </CollapsePanel>
                    </div>
                );
            })}
        </Fragment>
    );
};

export default VirtualTableOfContentsAnnex;
